import { PlusIcon, SidebarHideIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { ConversationList } from "./ConversationList.tsx";
import { MemoryPanel } from "./MemoryPanel.tsx";

export const SIDEBAR_PANELS = ["chats", "memoria"] as const;

export type SidebarPanel = (typeof SIDEBAR_PANELS)[number];

interface Props {
  readonly currentId: string | undefined;
  /**
   * Qué panel se ve. Lo manda el armazón y no un estado de aquí dentro: al
   * abrir una mecánica, que es una pantalla del centro, la columna tiene que
   * estar enseñando la memoria de donde salió.
   */
  readonly panel: SidebarPanel;
  onPanel: (panel: SidebarPanel) => void;
  onOpen: (conversationId: string) => void;
  onDeleted: (conversationId: string) => void;
  onNew: () => void;
  onHide: () => void;
}

/**
 * La columna izquierda del chat: lo que persiste entre conversaciones.
 *
 * El historial y la memoria van juntos a propósito. Son las dos capas que el
 * usuario puede inspeccionar (§11), y tenerlas en el mismo sitio deja claro
 * que lo que el asistente recuerda salió de lo que se habló.
 */
export function Sidebar({
  currentId,
  panel,
  onPanel,
  onOpen,
  onDeleted,
  onNew,
  onHide,
}: Props) {
  return (
    <aside className="flex w-72 shrink-0 flex-col gap-3 border-r bg-card p-3">
      <div className="flex items-center justify-between">
        <span className="font-semibold">Embajadores</span>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onHide}
          aria-label="Ocultar el panel"
        >
          <SidebarHideIcon />
        </Button>
      </div>

      {/* Empezar de cero y nada más: para cambiar de modo está el conmutador
          de arriba, que ya no se esconde. */}
      <Button variant="secondary" className="justify-start" onClick={onNew}>
        <PlusIcon /> Conversación nueva
      </Button>

      <Tabs
        value={panel}
        onValueChange={(value) => {
          onPanel(value as SidebarPanel);
        }}
      >
        <TabsList className="w-full">
          <TabsTrigger value="chats" className="flex-1">
            Chats
          </TabsTrigger>
          <TabsTrigger value="memoria" className="flex-1">
            Memoria
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Aquí NO hay scroll: lo pone cada panel. La memoria necesita dos
          --mecánicas y notas, por separado-- y un scroll común por encima los
          anularía, porque nada limitaría la altura de las secciones. */}
      <div className="flex min-h-0 flex-1 flex-col">
        {panel === "chats" ? (
          <ConversationList
            currentId={currentId}
            onOpen={onOpen}
            onDeleted={onDeleted}
          />
        ) : (
          <MemoryPanel />
        )}
      </div>
    </aside>
  );
}
